import { useLayoutEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { signInSuccess } from "../../redux/actions/authActions";
import { clearAuthRevoked } from "../../common/authSession";
import { applyPostSignInRedirect } from "../../common/signInRedirect";
import "../SignIn/SignIn.scss";

function decodeAuthHandoff(encoded) {
  const b64 = decodeURIComponent(encoded);
  const json = atob(b64);
  return JSON.parse(json);
}

const ERROR_MESSAGES = {
  student_only: "Career Scoops via ClassLink is for students only.",
  inactive: "Your account is inactive. Contact your school administrator.",
  missing_email: "Your ClassLink account does not include an email address.",
  not_configured: "ClassLink sign-in is not configured yet.",
  invalid_state: "Your login session expired. Please try again from ClassLink.",
  token_exchange_failed: "ClassLink sign-in failed. Please try again.",
  missing_token:
    "ClassLink sign-in did not finish. Use your school LaunchPad tile again (it should start at Career Scoops login, not this page).",
  invalid_handoff: "Could not complete ClassLink sign-in. Please try again from LaunchPad.",
  unknown: "Something went wrong during ClassLink sign-in.",
};

function backendClassLinkCallbackUrl() {
  const base = (process.env.REACT_APP_API_URL || "").replace(/\/?$/, "/");
  if (!base) return null;
  return `${base}classlink/callback/`;
}

export default function ClassLinkCallback() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useLayoutEffect(() => {
    const queryError = searchParams.get("classlink_error");
    if (queryError) {
      // Backend sends errors as ?classlink_error= — do not overwrite with missing_token.
      return;
    }

    const oauthCode = searchParams.get("code");
    const oauthState = searchParams.get("state");
    if (oauthCode && oauthState) {
      // Partner Portal sometimes registers the student app URL as redirect_uri; finish on API.
      const apiCallback = backendClassLinkCallbackUrl();
      if (apiCallback) {
        window.location.replace(`${apiCallback}${window.location.search}`);
        return;
      }
    }

    const hash = window.location.hash.startsWith("#")
      ? window.location.hash.slice(1)
      : window.location.hash;
    const hashParams = new URLSearchParams(hash);
    const auth = hashParams.get("auth");

    if (!auth) {
      navigate("/signin?classlink_error=missing_token", { replace: true });
      return;
    }

    try {
      const payload = decodeAuthHandoff(auth);
      if (!payload?.access) {
        navigate("/signin?classlink_error=missing_token", { replace: true });
        return;
      }
      clearAuthRevoked();
      dispatch(signInSuccess(payload));
      window.history.replaceState(null, "", "/classlink/callback");
      applyPostSignInRedirect({ loginResponse: payload });
    } catch {
      navigate("/signin?classlink_error=invalid_handoff", { replace: true });
    }
  }, [dispatch, navigate, searchParams]);

  const errorCode = searchParams.get("classlink_error");
  if (errorCode) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center px-6 text-center">
        <p className="text-lg font-semibold text-slate-900">ClassLink sign-in</p>
        <p className="mt-2 max-w-md text-sm text-slate-600">
          {ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.unknown}
        </p>
      </div>
    );
  }

  return (
    <section className="SignIn">
      <div className="SignIn-cont">
        <p className="SignIn-cont__title">Signing you in…</p>
        <p className="text-center text-sm text-slate-600">Completing ClassLink login.</p>
      </div>
    </section>
  );
}
